/*
 * Outbound — Pulse SDR workspace.
 *
 * Phase 1 (per Pulse_Outbound_Build_Brief.docx):
 *   Today        — SDR's prioritised task list (overdue first). Log the
 *                  outcome in-line to complete.
 *   Leads        — Full lead list for the current SDR. Pause / resume /
 *                  disposition / open deep-view.
 *   Lists        — CSV upload + list history.
 *   Reporting    — Per-SDR activity totals (Calls · Emails · LinkedIn ·
 *                  Meetings) over time; day or week granularity.
 *   Vitals       — Manager-facing team dashboard.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Phone, Mail, Linkedin, Calendar, Activity, Upload, Users as UsersIcon,
  TrendingUp, AlertTriangle, Pause, Play, CheckCircle2, Target, ListChecks,
  Flame, UserCheck, StickyNote, Copy, Trash2, Zap, Megaphone, MessageSquare,
  ChevronDown, ChevronRight, Droplet, Settings as SettingsIcon, UserPlus,
  Building2 as BuildingIcon, RefreshCw,
} from "lucide-react";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const CHANNEL_META = {
  call:     { icon: Phone,    label: "Call",     colour: "text-amber-700",  bg: "bg-amber-50",  border: "border-amber-200" },
  email:    { icon: Mail,     label: "Email",    colour: "text-blue-700",   bg: "bg-blue-50",   border: "border-blue-200" },
  linkedin: { icon: Linkedin, label: "LinkedIn", colour: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
  meeting:  { icon: Calendar, label: "Meeting",  colour: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  note:     { icon: Activity, label: "Note",     colour: "text-slate-700",  bg: "bg-slate-50",  border: "border-slate-200" },
};

const CALL_OUTCOMES = [
  { v: "connected",      l: "Connected" },
  { v: "voicemail",      l: "Voicemail left" },
  { v: "no_answer",      l: "No answer" },
  { v: "wrong_number",   l: "Wrong number" },
];

const EMAIL_OUTCOMES = [
  { v: "sent",           l: "Sent" },
  { v: "replied",        l: "Reply received" },
  { v: "bounced",        l: "Bounced" },
];

const LI_OUTCOMES = [
  { v: "connection_sent", l: "Connection request sent" },
  { v: "connection_accepted", l: "Connection accepted" },
  { v: "message_sent",   l: "Message sent" },
  { v: "no_response",    l: "No response" },
];

const DISPOSITIONS = [
  { v: "",                 l: "— (keep active)" },
  { v: "meeting_booked",   l: "Meeting Booked" },
  { v: "not_interested",   l: "Not Interested" },
  { v: "do_not_contact",   l: "Do Not Contact" },
  { v: "bad_data",         l: "Bad Data" },
  { v: "wrong_person",     l: "Wrong Person" },
];

const TABS = [
  { v: "today",     l: "Today",      icon: ListChecks },
  { v: "leads",     l: "Leads",      icon: UsersIcon },
  { v: "lists",     l: "Lists",      icon: Upload },
  { v: "reports",   l: "Reporting",  icon: TrendingUp },
  { v: "vitals",    l: "Vitals",     icon: Flame },
];

/* -------- QueuePanel: Feed me more + SDR cap editor + leave pause -------- */
function QueuePanel({ queue, sdrUserId, onReleased }) {
  const [releasing, setReleasing] = useState(false);
  const [capOpen, setCapOpen] = useState(false);
  const [capValue, setCapValue] = useState("");
  const [capSaving, setCapSaving] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  const [pauseSaving, setPauseSaving] = useState(false);
  const [resuming, setResuming] = useState(false);

  if (!queue) return null;
  const paused = !!queue.paused;
  const queued = queue.queued || 0;
  const releasedToday = queue.released_today || 0;
  const sdrCap = queue.sdr_cap || queue.default_cap || 25;
  const remainingToday = Math.max(0, sdrCap - releasedToday);
  const effectiveSdrId = sdrUserId || queue.sdr_user_id;

  const feedMe = async (listId = null) => {
    setReleasing(true);
    try {
      const r = await api.post("/outbound/queue/release", {
        sdr_user_id: sdrUserId || null,
        list_id: listId,
      });
      const n = r.data.released || 0;
      if (n === 0) {
        toast.message("Nothing left in the queue for that selection.");
      } else {
        toast.success(`Released ${n} lead${n === 1 ? "" : "s"} — tasks are now on Today`);
      }
      onReleased?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Release failed");
    } finally {
      setReleasing(false);
    }
  };

  const openCap = () => {
    setCapValue(queue.sdr_cap ? String(queue.sdr_cap) : "");
    setCapOpen(true);
  };
  const saveCap = async () => {
    const v = capValue.trim() === "" ? null : Number(capValue);
    if (v !== null && (!Number.isFinite(v) || v <= 0)) {
      toast.error("Cap must be a positive number or empty for default");
      return;
    }
    setCapSaving(true);
    try {
      await api.put(`/outbound/settings/sdr/${effectiveSdrId}`,
                    { daily_release_cap: v });
      toast.success(v ? `Daily cap set to ${v}` : "Daily cap reset to default");
      setCapOpen(false);
      onReleased?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save cap");
    } finally {
      setCapSaving(false);
    }
  };

  const pauseSdr = async () => {
    setPauseSaving(true);
    try {
      await api.post(`/outbound/settings/sdr/${effectiveSdrId}/pause`,
                     { reason: pauseReason.trim() || null });
      toast.success("Paused — tasks are hidden until you Resume");
      setPauseOpen(false);
      setPauseReason("");
      onReleased?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pause");
    } finally {
      setPauseSaving(false);
    }
  };

  const resumeSdr = async () => {
    setResuming(true);
    try {
      const r = await api.post(`/outbound/settings/sdr/${effectiveSdrId}/resume`);
      const n = r.data.bumped_tasks || 0;
      toast.success(n > 0
        ? `Resumed — ${n} task${n === 1 ? "" : "s"} bumped forward to ${r.data.bumped_to}`
        : "Resumed");
      onReleased?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume");
    } finally {
      setResuming(false);
    }
  };

  // --------- Paused SDR: show a prominent banner, nothing else ------------
  if (paused) {
    return (
      <div className="border border-amber-300 bg-amber-50 rounded-md p-4"
           data-testid="outbound-queue-paused">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="bg-amber-500 text-white rounded-md p-2">
              <Pause className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-bold text-amber-950">On leave — sequence paused</div>
              <div className="text-xs text-amber-900 mt-0.5">
                All Pulse tasks for this SDR are hidden until Resume. "Feed me more" is disabled.
              </div>
              <div className="text-[11px] font-mono text-amber-800 mt-1">
                Paused {queue.paused_at ? queue.paused_at.slice(0, 10) : ""}
                {queue.paused_reason ? ` · ${queue.paused_reason}` : ""}
              </div>
            </div>
          </div>
          <Button size="sm" onClick={resumeSdr} disabled={resuming}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="outbound-queue-resume-btn">
            <Play className="h-4 w-4 mr-1" />
            {resuming ? "Resuming…" : "Resume"}
          </Button>
        </div>
      </div>
    );
  }

  if (queued === 0) {
    return (
      <>
      <div className="flex items-center justify-between gap-3 border border-dashed border-slate-200 bg-white rounded-md px-3 py-2 text-xs text-slate-500"
           data-testid="outbound-queue-empty">
        <div className="flex items-center gap-2">
          <Droplet className="h-3.5 w-3.5 text-slate-400" />
          No queued leads — import a list on the Lists tab to feed this SDR.
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setPauseOpen(true)} type="button"
                  className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 text-[11px]"
                  data-testid="outbound-queue-pause-btn">
            <Pause className="h-3 w-3" /> Pause SDR
          </button>
          <button onClick={openCap} type="button"
                  className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700 text-[11px]"
                  data-testid="outbound-queue-cap-btn">
            <SettingsIcon className="h-3 w-3" /> Daily cap: {sdrCap}
          </button>
        </div>
      </div>
      <PauseDialog
        open={pauseOpen} onClose={() => setPauseOpen(false)}
        reason={pauseReason} setReason={setPauseReason}
        onConfirm={pauseSdr} saving={pauseSaving}
      />
      </>
    );
  }

  return (
    <div className="border border-sky-200 bg-sky-50/60 rounded-md p-3 space-y-2" data-testid="outbound-queue-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="bg-sky-600 text-white rounded-md p-1.5">
            <Droplet className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-bold text-sky-950">Queue · drip-feed</div>
            <div className="text-[11px] font-mono text-sky-900/70">
              <span data-testid="outbound-queue-queued">{queued}</span> queued ·
              <span className="ml-1" data-testid="outbound-queue-released-today">{releasedToday}</span> released today ·
              <span className="ml-1">cap {sdrCap}/day</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setPauseOpen(true)}
                  className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 text-[11px]"
                  data-testid="outbound-queue-pause-btn">
            <Pause className="h-3 w-3" /> Pause SDR
          </button>
          <button type="button" onClick={openCap}
                  className="inline-flex items-center gap-1 text-sky-800 hover:text-sky-900 text-[11px]"
                  data-testid="outbound-queue-cap-btn">
            <SettingsIcon className="h-3 w-3" /> Edit cap
          </button>
          <Button size="sm" onClick={() => feedMe(null)}
                  disabled={releasing || queued === 0}
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid="outbound-queue-feed-btn">
            <Droplet className="h-4 w-4 mr-1" />
            {releasing ? "Releasing…" :
              remainingToday > 0 && remainingToday < queued
                ? `Feed me ${Math.min(remainingToday, queued)} more`
                : `Feed me ${Math.min(sdrCap, queued)} more`}
          </Button>
        </div>
      </div>

      {queue.by_list && queue.by_list.length > 1 && (
        <div className="pt-1 space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-sky-900/70 font-semibold">Per list</div>
          {queue.by_list.map((bl) => (
            <div key={bl.list_id}
                 className="flex items-center justify-between gap-2 bg-white rounded px-2 py-1 border border-sky-100"
                 data-testid={`outbound-queue-list-${bl.list_id}`}>
              <div className="min-w-0 flex-1 truncate text-xs text-slate-700">
                {bl.list_name || "—"}
                {bl.campaign && (
                  <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-semibold text-pink-800">
                    <Megaphone className="h-3 w-3" />{bl.campaign}
                  </span>
                )}
                {bl.daily_release_cap && (
                  <span className="ml-2 text-[10px] font-mono text-sky-700">cap {bl.daily_release_cap}/day</span>
                )}
              </div>
              <span className="text-[11px] font-mono text-slate-500">{bl.queued} queued</span>
              <Button size="sm" variant="outline" className="h-6 text-[11px]"
                      disabled={releasing} onClick={() => feedMe(bl.list_id)}
                      data-testid={`outbound-queue-feed-list-${bl.list_id}`}>
                Feed me
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={capOpen} onOpenChange={setCapOpen}>
        <DialogContent className="max-w-sm" data-testid="outbound-queue-cap-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SettingsIcon className="h-4 w-4" /> Daily release cap
            </DialogTitle>
            <DialogDescription>
              How many fresh leads this SDR gets per Feed me more by default.
              A list's own cap overrides this when the list sets one.
              Leave blank to use the global default of {queue.default_cap || 25}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Cap per day</Label>
            <Input type="number" min="1" value={capValue}
                   onChange={(e) => setCapValue(e.target.value)}
                   placeholder={`${queue.default_cap || 25} (default)`}
                   data-testid="outbound-queue-cap-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCapOpen(false)} disabled={capSaving}>Cancel</Button>
            <Button onClick={saveCap} disabled={capSaving}
                    data-testid="outbound-queue-cap-save">
              {capSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PauseDialog
        open={pauseOpen} onClose={() => setPauseOpen(false)}
        reason={pauseReason} setReason={setPauseReason}
        onConfirm={pauseSdr} saving={pauseSaving}
      />
    </div>
  );
}

function PauseDialog({ open, onClose, reason, setReason, onConfirm, saving }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-sm" data-testid="outbound-queue-pause-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pause className="h-4 w-4 text-amber-600" /> Pause SDR sequence
          </DialogTitle>
          <DialogDescription>
            Hides every Today + Upcoming task for this SDR and blocks Feed me more.
            When Resumed, any tasks that fell due during the leave are bumped
            forward so you don't start Monday with 200 overdue items.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Reason (optional)</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="e.g. Annual leave until 24th Feb"
                 data-testid="outbound-queue-pause-reason" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={onConfirm} disabled={saving}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="outbound-queue-pause-confirm">
            <Pause className="h-4 w-4 mr-1" />
            {saving ? "Pausing…" : "Pause"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ========================== TODAY TAB ========================== */
function TodayTab({ sdrFilter, onSdrFilter, users }) {
  const [tasks, setTasks] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [upcomingCount, setUpcomingCount] = useState(0);
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const effectiveSdr = sdrFilter === "me" ? user?.user_id : sdrFilter;

  const load = async () => {
    setLoading(true);
    try {
      const params = effectiveSdr ? `?sdr_user_id=${effectiveSdr}` : "";
      const sep = params ? "&" : "?";
      const [today, up, q] = await Promise.all([
        api.get(`/outbound/tasks/today${params}`),
        api.get(`/outbound/tasks/upcoming${params}${sep}days=5`),
        api.get(`/outbound/queue/summary${params}`),
      ]);
      setTasks(today.data.tasks || []);
      setUpcoming(up.data.groups || []);
      setUpcomingCount(up.data.count || 0);
      setQueue(q.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Today list");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sdrFilter]);

  const overdue = tasks.filter((t) => t.overdue);
  const todayDue = tasks.filter((t) => !t.overdue);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-xs">SDR</Label>
          <Select value={sdrFilter} onValueChange={onSdrFilter}>
            <SelectTrigger className="w-56 h-8" data-testid="outbound-today-sdr-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="me">Me</SelectItem>
              {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs font-mono text-slate-500 flex items-center gap-3">
          <span className="flex items-center gap-1 text-sky-700">
            <Droplet className="h-3 w-3" /> Queued · {queue?.queued ?? 0}
          </span>
          <span className="flex items-center gap-1 text-red-700">
            <AlertTriangle className="h-3 w-3" /> Overdue · {overdue.length}
          </span>
          <span className="flex items-center gap-1 text-slate-700">
            <ListChecks className="h-3 w-3" /> Today · {todayDue.length}
          </span>
          <span className="flex items-center gap-1 text-slate-500">
            <Calendar className="h-3 w-3" /> Next 5 days · {upcomingCount}
          </span>
        </div>
      </div>

      <QueuePanel queue={queue} sdrUserId={effectiveSdr} onReleased={load} />

      {/* ===== TODAY — prominent, blue-accented focus block ===== */}
      <section data-testid="outbound-today-section"
               className="relative border-l-4 border-blue-600 bg-blue-50/40 rounded-r-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="bg-blue-600 text-white rounded-md p-1.5">
            <ListChecks className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-blue-950 tracking-tight">Today</h2>
            <p className="text-[11px] font-mono text-blue-900/70 uppercase tracking-wider">
              Dial these now · overdue first
            </p>
          </div>
          <span className="text-[11px] font-mono text-blue-900 bg-white border border-blue-200 px-2 py-0.5 rounded">
            {tasks.length} {tasks.length === 1 ? "task" : "tasks"}
          </span>
        </div>
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : tasks.length === 0 ? (
          <div className="bg-white border border-dashed border-blue-200 rounded-md py-8 text-center text-sm text-slate-500"
               data-testid="outbound-today-empty">
            All caught up — nothing due today.
          </div>
        ) : (
          <div className="space-y-2" data-testid="outbound-today-list">
            {[...overdue, ...todayDue].map((t) => (
              <TaskRow key={t.id} task={t} onDone={load} />
            ))}
          </div>
        )}
      </section>

      <UpcomingTasks groups={upcoming} total={upcomingCount} loading={loading} onDone={load} />
    </div>
  );
}

/* -------- Upcoming (next 5 business days) -------- */
function _fmtDueDate(iso) {
  // iso is YYYY-MM-DD — parse as local date, not UTC, so no off-by-one.
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, {
    weekday: "short", day: "2-digit", month: "short",
  });
}

function UpcomingTasks({ groups, total, loading, onDone }) {
  // Collapsed by default — the Today list is the primary focus. The chevron
  // in the trigger makes it obvious this is expandable.
  const [open, setOpen] = useState(false);
  if (loading) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid="outbound-upcoming-collapsible">
      <div className="border border-slate-200 bg-slate-50/60 rounded-md">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-slate-100/70 rounded-md"
            data-testid="outbound-upcoming-toggle"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              {open
                ? <ChevronDown className="h-4 w-4 text-slate-500" />
                : <ChevronRight className="h-4 w-4 text-slate-500" />}
              <Calendar className="h-4 w-4 text-slate-500" />
              Upcoming · next 5 business days
            </span>
            <span className="text-[11px] font-mono text-slate-600 bg-white border border-slate-200 px-2 py-0.5 rounded">
              {total} {total === 1 ? "task" : "tasks"}
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-3 pt-1 space-y-3">
            {total === 0 ? (
              <div className="text-sm text-slate-500 italic" data-testid="outbound-upcoming-empty">
                Nothing scheduled in the next 5 business days.
              </div>
            ) : (
              groups.map((g) => (
                <div key={g.due_date} className="space-y-1" data-testid={`outbound-upcoming-group-${g.due_date}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                      {_fmtDueDate(g.due_date)}
                    </div>
                    <div className="text-[10px] font-mono text-slate-400">{g.count} task{g.count === 1 ? "" : "s"}</div>
                  </div>
                  {g.tasks.length === 0 ? (
                    <div className="text-xs text-slate-400 pl-1">—</div>
                  ) : (
                    <div className="space-y-1.5 opacity-90">
                      {g.tasks.map((t) => (
                        <TaskRow key={t.id} task={t} onDone={onDone} />
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

/* -------- ContactIcons: compact row of click-to-copy icons for a lead -------- */
function ContactIcons({ lead, highlight = "", testIdPrefix = "outbound-contact" }) {
  const copy = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Clipboard blocked by browser");
    }
  };
  const items = [
    { channel: "call",     icon: Phone,    value: lead?.phone,
      label: "Phone",       colour: "amber",  action: "dial"  },
    { channel: "email",    icon: Mail,     value: lead?.email,
      label: "Email",       colour: "blue",   action: "copy"  },
    { channel: "linkedin", icon: Linkedin, value: lead?.linkedin_url,
      label: "LinkedIn URL", colour: "indigo", action: "open" },
  ].filter((it) => !!it.value);
  if (items.length === 0) {
    return <span className="text-[11px] italic text-slate-400">No contact details</span>;
  }
  const ringMap = {
    amber:  "ring-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100",
    blue:   "ring-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100",
    indigo: "ring-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
  };
  const idleMap = {
    amber:  "text-amber-700 hover:bg-amber-50",
    blue:   "text-blue-700 hover:bg-blue-50",
    indigo: "text-indigo-700 hover:bg-indigo-50",
  };
  return (
    <div className="flex items-center gap-1">
      {items.map(({ channel, icon: Ico, value, label, colour, action }) => {
        const isPrimary = channel === highlight;
        const cls = isPrimary
          ? `ring-2 ${ringMap[colour]}`
          : idleMap[colour];
        const base = `inline-flex items-center justify-center h-7 rounded-md transition-colors ${cls}`;
        if (action === "dial") {
          // Phone — click rings the hooked phone system directly. Long-press
          // / secondary click can still copy via browser.
          const digits = String(value).replace(/[^\d+]/g, "");
          return (
            <a
              key={channel}
              href={`tel:${digits}`}
              onClick={(e) => e.stopPropagation()}
              title={`Dial ${value}`}
              aria-label={`Dial ${label}`}
              className={`${base} px-2 gap-1 font-mono text-[11px]`}
              data-testid={`${testIdPrefix}-dial`}
            >
              <Ico className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{value}</span>
            </a>
          );
        }
        if (action === "open") {
          // LinkedIn — open the URL; a sibling copy chip lives next to it
          return (
            <span key={channel} className="flex items-center">
              <a
                href={value}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                title={`Open ${label}`}
                aria-label={`Open ${label}`}
                className={`${base} w-7`}
                data-testid={`${testIdPrefix}-open-${channel}`}
              >
                <Ico className="h-3.5 w-3.5" />
              </a>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); copy(value, label); }}
                title={`Copy ${label}`}
                aria-label={`Copy ${label}`}
                className="inline-flex items-center justify-center h-7 w-5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                data-testid={`${testIdPrefix}-copy-${channel}`}
              >
                <Copy className="h-3 w-3" />
              </button>
            </span>
          );
        }
        // Default: copy (email)
        return (
          <button
            key={channel}
            type="button"
            onClick={(e) => { e.stopPropagation(); copy(value, label); }}
            title={`Copy ${label}: ${value}`}
            aria-label={`Copy ${label}`}
            className={`${base} w-7`}
            data-testid={`${testIdPrefix}-copy-${channel}`}
          >
            <Ico className="h-3.5 w-3.5" />
          </button>
        );
      })}
    </div>
  );
}

function CompanyBioButton({ company, domain, testIdPrefix = "outbound-task" }) {
  // Dropdown that lazy-loads an AI briefing on the prospect company. The
  // briefing is anchored on the lead's domain/URL (unambiguous) rather than
  // just the name — "ACME London Ltd" and "ACME NY Inc" cache separately.
  // Repeat opens are instant (cached server-side); Refresh re-spends an LLM
  // call for a fresh pass.
  const [open, setOpen] = useState(false);
  const [bio, setBio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const panelRef = React.useRef(null);
  const btnRef = React.useRef(null);

  const fetchBio = async (refresh = false) => {
    if (!company) { setError("No company on this lead"); return; }
    setLoading(true); setError(null);
    try {
      const params = {};
      if (domain) params.url = domain;
      if (refresh) params.refresh = 1;
      const r = await api.get(`/outbound/companies/${encodeURIComponent(company)}/bio`, { params });
      setBio(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load company briefing");
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !bio && !loading) fetchBio(false);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)
          && btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const row = (label, value, testid) => (
    <div className="py-1.5 border-b border-slate-100 last:border-b-0" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-0.5">{label}</div>
      <div className="text-xs text-slate-700 leading-snug">{value || "—"}</div>
    </div>
  );

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className={`p-1.5 rounded border ${open ? "bg-sky-50 border-sky-300 text-sky-700" : "border-slate-200 text-slate-500 hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200"}`}
        title="Company briefing"
        data-testid={`${testIdPrefix}-company-bio`}
        aria-label="Company briefing"
      >
        <BuildingIcon className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 mt-1 z-40 w-96 bg-white border border-slate-200 rounded-lg shadow-xl text-xs"
          data-testid={`${testIdPrefix}-company-bio-panel`}
        >
          {/* Header */}
          <div className="px-3 py-2 border-b border-slate-200 flex items-start justify-between gap-2 bg-slate-50 rounded-t-lg">
            <div className="min-w-0">
              <div className="font-heading text-sm font-bold text-slate-800 truncate">
                {company || "(no company)"}
              </div>
              {bio?.domain && (
                <div className="text-[10px] font-mono text-sky-700 truncate">
                  {bio.domain}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => fetchBio(true)}
              disabled={loading || !company}
              className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-40 shrink-0"
              title="Re-generate briefing"
              data-testid={`${testIdPrefix}-company-bio-refresh`}
            >
              <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
          {/* Body */}
          <div className="px-3 py-2">
            {loading && !bio && (
              <div className="py-6 text-center text-slate-500 italic">
                Researching {company}…
              </div>
            )}
            {error && (
              <div className="py-3 text-rose-600">{error}</div>
            )}
            {bio && !loading && (
              <>
                {/* Summary gets prime real estate */}
                <div className="pb-2 border-b border-slate-100"
                     data-testid={`${testIdPrefix}-company-bio-summary`}>
                  <div className="text-xs text-slate-800 leading-relaxed">
                    {bio.summary || "—"}
                  </div>
                </div>
                {row("Size · Geography", bio.size_geo, `${testIdPrefix}-company-bio-size`)}
                {row("Industry", bio.industry, `${testIdPrefix}-company-bio-industry`)}
                {row("Recent", bio.recent, `${testIdPrefix}-company-bio-recent`)}
                {row("Pain point to explore", bio.pain_point, `${testIdPrefix}-company-bio-pain`)}
                <div className="mt-2 text-[10px] font-mono text-slate-400">
                  {bio.generated_at ? new Date(bio.generated_at).toLocaleDateString() : "—"} · AI briefing · verify before quoting
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LogLeadButton({ task, onDone }) {
  const [open, setOpen] = useState(false);
  const lead = task.lead || {};
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
        data-testid={`outbound-task-log-lead-${task.id}`}
        title="Log this contact as a Sales lead and remove the company from the sequence"
      >
        <UserPlus className="h-4 w-4 mr-1" /> Log Lead
      </Button>
      {open && (
        <LogLeadDialog
          task={task}
          lead={lead}
          onClose={() => setOpen(false)}
          onLogged={() => { setOpen(false); onDone?.(); }}
        />
      )}
    </>
  );
}

function LogLeadDialog({ task, lead, onClose, onLogged }) {
  const [meta, setMeta] = useState({ statuses: [], industries: [], next_actions: [], lead_sources: [], products: [] });
  const [f, setF] = useState({
    first_name: lead.first_name || "",
    last_name: lead.last_name || "",
    company: lead.company || "",
    job_title: lead.title || "",
    email: lead.email || "",
    phone: lead.phone || "",
    lead_source: "Outbound Pulse",
    status: "SQL",
    industry: "",
    number_of_users: "",
    next_action: "",
    next_action_date: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const setField = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/leads/meta");
        setMeta(r.data || {});
      } catch (e) {
        /* non-fatal — the dialog still functions with free-form inputs */
      }
    })();
  }, []);

  const save = async () => {
    if (!f.first_name || !f.last_name || !f.company) {
      toast.error("First name, last name and company are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...f,
        number_of_users: f.number_of_users === "" ? null : Number(f.number_of_users),
      };
      // Normalise empty optional strings → null so the backend sees proper absence.
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const r = await api.post(`/outbound/tasks/${task.id}/log-as-lead`, payload);
      const removed = r.data?.removed_from_sequence || 0;
      toast.success(
        removed > 1
          ? `Lead logged — ${removed} contacts at ${f.company} removed from the sequence`
          : "Lead logged — contact removed from the sequence",
      );
      onLogged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log lead");
    } finally {
      setBusy(false);
    }
  };

  const statusOptions = (meta.statuses || []).filter((s) => s !== "Converted" && s !== "Dead");
  const sourceOptions = meta.lead_sources && meta.lead_sources.length
    ? meta.lead_sources
    : ["Outbound Pulse"];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="outbound-log-lead-dialog">
        <DialogHeader>
          <DialogTitle>Log as Sales lead</DialogTitle>
          <DialogDescription>
            Create a Sales lead with these details. {f.company
              ? <>Every outbound contact at <strong>{f.company}</strong> will also be removed from the Pulse sequence.</>
              : "Enter a company name below."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">First name *</Label>
              <Input value={f.first_name} onChange={(e) => setField("first_name", e.target.value)} data-testid="log-lead-first" />
            </div>
            <div>
              <Label className="text-xs">Last name *</Label>
              <Input value={f.last_name} onChange={(e) => setField("last_name", e.target.value)} data-testid="log-lead-last" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Company *</Label>
            <Input value={f.company} onChange={(e) => setField("company", e.target.value)} data-testid="log-lead-company" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Job title</Label>
              <Input value={f.job_title} onChange={(e) => setField("job_title", e.target.value)} data-testid="log-lead-title" />
            </div>
            <div>
              <Label className="text-xs">Lead source</Label>
              <Select value={f.lead_source || ""} onValueChange={(v) => setField("lead_source", v)}>
                <SelectTrigger data-testid="log-lead-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sourceOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={f.email} onChange={(e) => setField("email", e.target.value)} data-testid="log-lead-email" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={f.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="log-lead-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Number of users</Label>
              <Input type="number" value={f.number_of_users} onChange={(e) => setField("number_of_users", e.target.value)} data-testid="log-lead-users" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={f.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger data-testid="log-lead-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(statusOptions.length ? statusOptions : ["Unqualified", "MQL", "SQL"]).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Industry</Label>
            <Select value={f.industry || "__none__"} onValueChange={(v) => setField("industry", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="log-lead-industry"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {(meta.industries || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Next action</Label>
              <Select value={f.next_action || "__none__"} onValueChange={(v) => setField("next_action", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="log-lead-next-action"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {(meta.next_actions || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Action date</Label>
              <Input type="date" value={f.next_action_date} onChange={(e) => setField("next_action_date", e.target.value)} data-testid="log-lead-next-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={3} value={f.notes} onChange={(e) => setField("notes", e.target.value)}
                      placeholder="What triggered logging this as a lead? Any hooks for Sales?"
                      data-testid="log-lead-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy || !f.first_name || !f.last_name || !f.company}
                  data-testid="log-lead-submit">
            {busy ? "Logging…" : "Log Lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskRow({ task, onDone }) {
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [disposition, setDisposition] = useState("");
  const [saving, setSaving] = useState(false);
  const [priorNotes, setPriorNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const meta = CHANNEL_META[task.channel] || CHANNEL_META.note;
  const Icon = meta.icon;
  const outcomes = task.channel === "call" ? CALL_OUTCOMES
                 : task.channel === "email" ? EMAIL_OUTCOMES
                 : task.channel === "linkedin" ? LI_OUTCOMES : [];

  // Pull prior notes when the dialog opens so the SDR sees context from
  // earlier touches on the same lead before writing a new one.
  useEffect(() => {
    if (!open || !task.lead_id) return;
    let cancelled = false;
    setLoadingNotes(true);
    (async () => {
      try {
        const r = await api.get(`/outbound/leads/${task.lead_id}/notes`);
        if (!cancelled) setPriorNotes(r.data || []);
      } catch (_) {
        if (!cancelled) setPriorNotes([]);
      } finally {
        if (!cancelled) setLoadingNotes(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, task.lead_id]);

  const save = async () => {
    setSaving(true);
    try {
      // Shadcn <Select> can't carry an empty string, so "none" is our
      // sentinel for "keep lead active" / "no outcome". Normalise before
      // the API call so the backend sees null, not literal "none".
      const dispositionClean = disposition && disposition !== "none" ? disposition : null;
      await api.post(`/outbound/tasks/${task.id}/complete`, {
        outcome: outcome || null,
        notes: notes.trim() || null,
        disposition: dispositionClean,
      });
      toast.success(`Task #${task.step} logged`);
      setOpen(false);
      setOutcome(""); setNotes(""); setDisposition("");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log task");
    } finally {
      setSaving(false);
    }
  };

  const lead = task.lead || {};
  return (
    <Card
      className={`${meta.border} ${task.overdue ? "ring-2 ring-red-300" : ""}`}
      data-testid={`outbound-task-${task.id}`}
    >
      <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`${meta.bg} ${meta.colour} p-2 rounded-lg flex items-center justify-center shrink-0`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">
              <button
                type="button"
                onClick={() => setDetailOpen(true)}
                className="text-slate-900 hover:text-blue-700 hover:underline underline-offset-2 decoration-dotted focus:outline-none focus:text-blue-700 cursor-pointer text-left"
                title="Open contact · notes timeline"
                data-testid={`outbound-task-open-contact-${task.id}`}
              >
                {lead.first_name} {lead.last_name}
              </button>
              {lead.title && (
                <span className="text-xs font-normal text-slate-600 ml-1.5"
                      data-testid={`outbound-task-title-${task.id}`}>· {lead.title}</span>
              )}
              <span className="text-xs font-mono text-slate-500 ml-2">· {lead.company}</span>
              {lead.campaign && (
                <span className="ml-2 inline-flex items-center gap-1 bg-pink-50 text-pink-800 border border-pink-200 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
                      data-testid={`outbound-task-campaign-${task.id}`}>
                  <Megaphone className="h-3 w-3" /> {lead.campaign}
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
              <span>Step {task.step}/15 · {task.action}</span>
              {task.overdue && <span className="text-red-700 font-semibold">· OVERDUE</span>}
              <span className="font-mono">· due {task.due_date}</span>
              {lead.notes_count > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-800"
                      title="Prior notes recorded on this contact — open Log to read them"
                      data-testid={`outbound-task-notes-chip-${task.id}`}>
                  <MessageSquare className="h-3 w-3" /> {lead.notes_count} note{lead.notes_count === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ContactIcons lead={lead} highlight={task.channel} testIdPrefix={`outbound-task-${task.id}`} />
          <CompanyBioButton
            company={lead.company}
            domain={lead.domain || lead.website || lead.linkedin_url || lead.email}
            testIdPrefix={`outbound-task-${task.id}`}
          />
          <LogLeadButton task={task} onDone={onDone} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" data-testid={`outbound-task-complete-${task.id}`}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Log
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="outbound-task-complete-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Icon className="h-4 w-4" /> Log {meta.label.toLowerCase()}
                </DialogTitle>
                <DialogDescription>
                  {lead.first_name} {lead.last_name}
                  {lead.title ? ` · ${lead.title}` : ""} · {lead.company} · Step {task.step}/15
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                {/* Previous notes — context the SDR needs before writing a new one. */}
                <div className="space-y-1" data-testid="outbound-prior-notes-panel">
                  <div className="flex items-center justify-between">
                    <Label className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
                      Previous notes {priorNotes.length > 0 && <span className="text-slate-900">· {priorNotes.length}</span>}
                    </Label>
                  </div>
                  <div className="rounded-md border border-slate-200 bg-slate-50/60 max-h-40 overflow-y-auto p-2">
                    {loadingNotes ? (
                      <div className="text-[11px] italic text-slate-400">Loading…</div>
                    ) : priorNotes.length === 0 ? (
                      <div className="text-[11px] italic text-slate-400" data-testid="outbound-prior-notes-empty">
                        No earlier notes on this contact — this will be the first.
                      </div>
                    ) : (
                      <ul className="space-y-2">
                        {priorNotes.map((n) => (
                          <li key={n.id || n.timestamp}
                              className="text-xs leading-snug border-l-2 border-blue-300 pl-2"
                              data-testid={`outbound-prior-note-${n.id || n.timestamp}`}>
                            <div className="text-[10px] font-mono text-slate-500 flex items-center gap-1.5">
                              {n.timestamp ? new Date(n.timestamp).toLocaleString() : ""}
                              {n.author_name && <span>· {n.author_name}</span>}
                              {n.step != null && <span>· step {n.step}</span>}
                              {n.channel && <span className="uppercase">· {n.channel}</span>}
                            </div>
                            <div className="text-slate-800 whitespace-pre-wrap">{n.body}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                {outcomes.length > 0 && (
                  <div className="space-y-1">
                    <Label>Outcome</Label>
                    <Select value={outcome} onValueChange={setOutcome}>
                      <SelectTrigger data-testid="outbound-outcome-select"><SelectValue placeholder="Pick an outcome" /></SelectTrigger>
                      <SelectContent>
                        {outcomes.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1">
                  <Label>Notes</Label>
                  <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                            placeholder="What happened? Hooks for next touch?" data-testid="outbound-notes" />
                </div>
                <div className="space-y-1">
                  <Label>Disposition</Label>
                  <Select value={disposition} onValueChange={setDisposition}>
                    <SelectTrigger data-testid="outbound-disposition-select"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      {DISPOSITIONS.map((d) => <SelectItem key={d.v || "none"} value={d.v || "none"}>{d.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-slate-500">
                    Terminal dispositions pause the sequence and remove this lead from the Today list.
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid="outbound-task-submit">
                  {saving ? "Saving…" : "Mark done"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
      <LeadDetailDialog
        lead={detailOpen ? lead : null}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onChanged={onDone}
      />
    </Card>
  );
}

/* ========================== LEADS TAB ========================== */
function LeadsTab({ users }) {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("");
  const [sdr, setSdr] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (sdr)    params.set("sdr_user_id", sdr);
    const r = await api.get(`/outbound/leads${params.toString() ? "?" + params.toString() : ""}`);
    setLeads(r.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, sdr]);

  const pauseResume = async (lead) => {
    await api.post(`/outbound/leads/${lead.id}/${lead.status === "paused" ? "resume" : "pause"}`);
    toast.success(`${lead.first_name} ${lead.status === "paused" ? "resumed" : "paused"}`);
    load();
  };

  const selectedLead = leads.find((l) => l.id === selectedId) || null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-xs">Status</Label>
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 h-8" data-testid="outbound-leads-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="queued">Queued (not yet released)</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="meeting_booked">Meeting booked</SelectItem>
              <SelectItem value="converted">Converted (SQL)</SelectItem>
              <SelectItem value="not_interested">Not interested</SelectItem>
              <SelectItem value="do_not_contact">Do not contact</SelectItem>
              <SelectItem value="bad_data">Bad data</SelectItem>
              <SelectItem value="completed">Sequence complete</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">SDR</Label>
          <Select value={sdr || "all"} onValueChange={(v) => setSdr(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All SDRs</SelectItem>
              {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs font-mono text-slate-500 ml-auto">{leads.length} lead{leads.length === 1 ? "" : "s"}</div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-hidden">
          <table className="w-full text-sm" data-testid="outbound-leads-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Company</th>
                <th className="px-3 py-2 text-left">Title</th>
                <th className="px-3 py-2 text-left">Owner</th>
                <th className="px-3 py-2 text-left">Step</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-500 italic">No leads match this filter.</td></tr>
              )}
              {leads.map((l) => (
                <tr key={l.id}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelectedId(l.id)}
                    data-testid={`outbound-lead-${l.id}`}>
                  <td className="px-3 py-2 font-medium text-slate-900">{l.first_name} {l.last_name}</td>
                  <td className="px-3 py-2 text-slate-700">{l.company}</td>
                  <td className="px-3 py-2 text-slate-600">{l.title || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{l.sdr_name || <span className="text-slate-400">Unassigned</span>}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">{l.current_step || 0}/15</td>
                  <td className="px-3 py-2">
                    <StatusPill status={l.status} />
                  </td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost"
                              onClick={() => setSelectedId(l.id)}
                              data-testid={`outbound-lead-open-${l.id}`}
                              title="Open lead · notes · convert">
                        <StickyNote className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => pauseResume(l)}
                              data-testid={`outbound-lead-toggle-${l.id}`}>
                        {l.status === "paused" ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <LeadDetailDialog
        lead={selectedLead}
        open={!!selectedLead}
        onClose={() => setSelectedId(null)}
        onChanged={load}
      />
    </div>
  );
}

/* -------- Lead detail dialog: notes timeline + convert-to-SQL -------- */
function LeadDetailDialog({ lead, open, onClose, onChanged }) {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertNote, setConvertNote] = useState("");

  useEffect(() => {
    if (!lead) { setNotes([]); return; }
    (async () => {
      try {
        const r = await api.get(`/outbound/leads/${lead.id}/notes`);
        setNotes(r.data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load notes");
      }
    })();
  }, [lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!lead) return null;

  const addNote = async () => {
    const body = draft.trim();
    if (!body) return;
    setSaving(true);
    try {
      const r = await api.post(`/outbound/leads/${lead.id}/notes`, { body });
      setNotes((prev) => [r.data, ...prev]);
      setDraft("");
      toast.success("Note added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add note");
    } finally {
      setSaving(false);
    }
  };

  const canConvert = lead.status === "meeting_booked";
  const alreadyConverted = lead.status === "converted" || !!lead.converted_lead_id;

  const convert = async () => {
    setConverting(true);
    try {
      await api.post(`/outbound/leads/${lead.id}/convert`,
        { notes: convertNote.trim() || null });
      toast.success(`${lead.first_name} converted to SQL in Leads`);
      setConvertOpen(false);
      setConvertNote("");
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    } finally {
      setConverting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="outbound-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <UsersIcon className="h-4 w-4" />
            {lead.first_name} {lead.last_name}
            <span className="text-xs font-mono text-slate-500 ml-1">· {lead.company}</span>
            <span className="ml-2"><StatusPill status={lead.status} /></span>
            {lead.campaign && (
              <span className="inline-flex items-center gap-1 bg-pink-50 text-pink-800 border border-pink-200 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider">
                <Megaphone className="h-3 w-3" /> {lead.campaign}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {lead.title ? `${lead.title} · ` : ""}Owner: {lead.sdr_name || "Unassigned"} · Step {lead.current_step || 0}/15
          </DialogDescription>
        </DialogHeader>

        {/* Contact row */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-slate-600 space-y-0.5">
            {lead.email && (
              <a href={`mailto:${lead.email}`}
                 className="flex items-center gap-1 text-blue-700 hover:underline">
                <Mail className="h-3 w-3" /> {lead.email}
              </a>
            )}
            {lead.phone && (
              <a href={`tel:${String(lead.phone).replace(/[^\d+]/g, "")}`}
                 className="flex items-center gap-1 text-amber-700 hover:underline font-mono"
                 data-testid={`outbound-lead-${lead.id}-dial`}>
                <Phone className="h-3 w-3" /> {lead.phone}
              </a>
            )}
            {lead.linkedin_url && (
              <a href={lead.linkedin_url} target="_blank" rel="noreferrer"
                 className="flex items-center gap-1 text-indigo-700 hover:underline">
                <Linkedin className="h-3 w-3" /> Open LinkedIn →
              </a>
            )}
          </div>
          <ContactIcons lead={lead} testIdPrefix={`outbound-lead-${lead.id}`} />
        </div>

        {/* Convert banner */}
        {alreadyConverted ? (
          <div className="bg-teal-50 border border-teal-200 text-teal-800 rounded p-3 flex items-center gap-2 text-xs"
               data-testid="outbound-lead-already-converted">
            <UserCheck className="h-4 w-4" />
            This lead has been converted to a Sales-Qualified Lead in the Leads module.
            The Pulse sequence is frozen.
          </div>
        ) : canConvert ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 flex items-center justify-between gap-3">
            <div className="text-xs text-emerald-900">
              <div className="font-semibold flex items-center gap-1"><Calendar className="h-3 w-3" /> Meeting booked</div>
              <div>Ready to hand off to Sales — convert to a Sales-Qualified Lead (SQL).</div>
            </div>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => setConvertOpen(true)}
                    data-testid="outbound-lead-convert-btn">
              <UserCheck className="h-4 w-4 mr-1" /> Convert to SQL
            </Button>
          </div>
        ) : null}

        {/* Notes timeline */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <StickyNote className="h-4 w-4" /> Notes
            <span className="text-xs font-mono text-slate-500">· {notes.length}</span>
          </div>
          <div className="space-y-1">
            <Textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)}
                      placeholder="Add a note — what did they say? Hooks for next touch?"
                      data-testid="outbound-lead-note-input" />
            <div className="flex justify-end">
              <Button size="sm" onClick={addNote} disabled={saving || !draft.trim()}
                      data-testid="outbound-lead-note-submit">
                {saving ? "Saving…" : "Add note"}
              </Button>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto space-y-2 border-t border-slate-100 pt-2"
               data-testid="outbound-lead-notes-list">
            {notes.length === 0 ? (
              <div className="text-xs text-slate-500 italic">No notes yet.</div>
            ) : (
              notes.map((n) => (
                <div key={n.id} className="bg-slate-50 border border-slate-100 rounded p-2">
                  <div className="text-[11px] font-mono text-slate-500 flex items-center justify-between">
                    <span>{n.author_name || "?"}</span>
                    <span>{(n.timestamp || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap mt-0.5">{n.body}</div>
                </div>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>

        {/* Convert confirm dialog */}
        <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
          <DialogContent className="max-w-md" data-testid="outbound-lead-convert-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-emerald-700" /> Convert to SQL
              </DialogTitle>
              <DialogDescription>
                Creates a Sales-Qualified Lead in the Leads module for{" "}
                <span className="font-semibold">{lead.first_name} {lead.last_name}</span> at{" "}
                <span className="font-semibold">{lead.company}</span>. The Pulse sequence will be frozen.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label>Handover note (optional)</Label>
              <Textarea rows={3} value={convertNote} onChange={(e) => setConvertNote(e.target.value)}
                        placeholder="Context for the account manager — meeting time, pain points, who else is on the call…"
                        data-testid="outbound-lead-convert-note" />
              <div className="text-[11px] text-slate-500">
                All existing notes on this lead will also be copied across.
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConvertOpen(false)} disabled={converting}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={convert} disabled={converting}
                      data-testid="outbound-lead-convert-confirm">
                {converting ? "Converting…" : "Convert"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_COLOURS = {
  queued: "bg-slate-100 text-slate-700",
  active: "bg-blue-100 text-blue-800",
  paused: "bg-amber-100 text-amber-800",
  meeting_booked: "bg-emerald-100 text-emerald-800",
  converted: "bg-teal-100 text-teal-800",
  not_interested: "bg-slate-200 text-slate-700",
  do_not_contact: "bg-red-100 text-red-800",
  bad_data: "bg-rose-100 text-rose-800",
  wrong_person: "bg-orange-100 text-orange-800",
  completed: "bg-indigo-100 text-indigo-800",
};
function StatusPill({ status }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${STATUS_COLOURS[status] || "bg-slate-100 text-slate-700"}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

/* ========================== LISTS TAB ========================== */
function ListCapCell({ list, onSave }) {
  // Inline-edit the list's daily release cap. Shows the saved value by
  // default; dirty state surfaces a Save button so no stray keystrokes are
  // ever posted. Empty value clears the override and falls back to the
  // SDR-level default (25/day).
  const saved = list.daily_release_cap ?? "";
  const [val, setVal] = useState(String(saved));
  const [editing, setEditing] = useState(false);
  useEffect(() => { setVal(String(list.daily_release_cap ?? "")); setEditing(false); },
            [list.daily_release_cap]);
  const dirty = String(val) !== String(saved);
  const commit = async () => {
    await onSave(val);
    setEditing(false);
  };
  const cancel = () => { setVal(String(saved)); setEditing(false); };
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={`outbound-list-cap-${list.id}`}
        className="font-mono text-sky-700 hover:text-sky-900 hover:underline cursor-pointer"
        title="Click to change the daily release cap"
      >
        {list.daily_release_cap
          ? list.daily_release_cap
          : <span className="text-slate-400">25<span className="text-[9px] ml-1">default</span></span>}
      </button>
    );
  }
  return (
    <div className="flex items-center justify-end gap-1" data-testid={`outbound-list-cap-editor-${list.id}`}>
      <Input
        type="number"
        min={1}
        max={1000}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") cancel(); }}
        autoFocus
        className="h-7 w-16 text-right font-mono text-xs px-1"
        placeholder="25"
        data-testid={`outbound-list-cap-input-${list.id}`}
      />
      <Button size="sm" variant="ghost"
              onClick={commit}
              disabled={!dirty}
              className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"
              data-testid={`outbound-list-cap-save-${list.id}`}
              title="Save new cap">
        <CheckCircle2 className="h-3.5 w-3.5" />
      </Button>
      <Button size="sm" variant="ghost"
              onClick={cancel}
              className="h-7 px-2 text-slate-500 hover:bg-slate-100"
              title="Cancel">
        ×
      </Button>
    </div>
  );
}

function ListsTab({ users, onImported }) {
  const [lists, setLists] = useState([]);
  const [name, setName] = useState("");
  const [campaign, setCampaign] = useState("");
  const [sdr, setSdr] = useState("");
  const [cap, setCap] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  // Apollo state
  const [aName, setAName] = useState("");
  const [aCampaign, setACampaign] = useState("");
  const [aListId, setAListId] = useState("");
  const [aSdr, setASdr] = useState("");
  const [aCap, setACap] = useState("");
  const [aBusy, setABusy] = useState(false);

  const load = async () => {
    const r = await api.get("/outbound/lists");
    setLists(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file || !name.trim()) {
      toast.error("List name and CSV file are required");
      return;
    }
    const fd = new FormData();
    fd.append("name", name.trim());
    if (campaign.trim()) fd.append("campaign", campaign.trim());
    if (sdr) fd.append("sdr_user_id", sdr);
    if (cap && Number(cap) > 0) fd.append("daily_release_cap", String(Number(cap)));
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post("/outbound/lists/upload", fd);
      toast.success(`Queued ${r.data.imported} leads · ${r.data.duplicates} duplicates skipped · ${r.data.skipped} rows invalid — none active yet, use Feed me more`);
      setName(""); setCampaign(""); setFile(null); setSdr(""); setCap("");
      load(); onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const apolloPull = async () => {
    if (!aName.trim() || !aListId.trim()) {
      toast.error("List name and Apollo List ID are required");
      return;
    }
    const fd = new FormData();
    fd.append("name", aName.trim());
    fd.append("apollo_list_id", aListId.trim());
    if (aCampaign.trim()) fd.append("campaign", aCampaign.trim());
    if (aSdr) fd.append("sdr_user_id", aSdr);
    if (aCap && Number(aCap) > 0) fd.append("daily_release_cap", String(Number(aCap)));
    setABusy(true);
    try {
      const r = await api.post("/outbound/lists/apollo-pull", fd);
      toast.success(`Queued ${r.data.imported || 0} leads from Apollo — none active yet, use Feed me more`);
      setAName(""); setACampaign(""); setAListId(""); setASdr(""); setACap("");
      load(); onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apollo pull failed");
    } finally {
      setABusy(false);
    }
  };

  const deleteList = async (lst) => {
    try {
      const r = await api.delete(`/outbound/lists/${lst.id}`);
      toast.success(`"${lst.name}" deleted — ${r.data.leads_deleted} lead${r.data.leads_deleted === 1 ? "" : "s"}, ${r.data.open_tasks_deleted} open task${r.data.open_tasks_deleted === 1 ? "" : "s"} removed`);
      load(); onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const updateListCap = async (lst, nextCap) => {
    const n = nextCap === "" || nextCap == null ? null : Number(nextCap);
    if (n != null && (!Number.isFinite(n) || n < 1 || n > 1000)) {
      toast.error("Daily cap must be between 1 and 1000");
      return;
    }
    try {
      await api.patch(`/outbound/lists/${lst.id}`, { daily_release_cap: n });
      toast.success(n
        ? `"${lst.name}" cap set to ${n}/day`
        : `"${lst.name}" cap cleared — falls back to the SDR default`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update cap");
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid lg:grid-cols-2 gap-5">
        {/* ----- CSV uploader ----- */}
        <Card data-testid="outbound-list-csv-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Upload className="h-4 w-4 text-slate-600" /> Import leads via CSV
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-xs text-slate-500">
              Required columns (case-insensitive): <code className="bg-slate-100 px-1 rounded">first_name</code>,{" "}
              <code className="bg-slate-100 px-1 rounded">last_name</code>,{" "}
              <code className="bg-slate-100 px-1 rounded">company</code>. Recommended:{" "}
              <code className="bg-slate-100 px-1 rounded">email</code>,{" "}
              <code className="bg-slate-100 px-1 rounded">title</code>,{" "}
              <code className="bg-slate-100 px-1 rounded">phone</code>,{" "}
              <code className="bg-slate-100 px-1 rounded">linkedin_url</code>,{" "}
              <code className="bg-slate-100 px-1 rounded">domain</code>.
              Duplicates on email are automatically skipped.
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>List name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="e.g. London MSPs Jan 2026" data-testid="outbound-list-name" />
              </div>
              <div className="space-y-1">
                <Label className="flex items-center gap-1">
                  <Megaphone className="h-3 w-3 text-pink-700" /> Campaign (optional)
                </Label>
                <Input value={campaign} onChange={(e) => setCampaign(e.target.value)}
                       placeholder="e.g. Barracuda XDR Q1 push" data-testid="outbound-list-campaign" />
              </div>
              <div className="space-y-1">
                <Label>Assign to SDR (optional)</Label>
                <Select value={sdr || "unassigned"} onValueChange={(v) => setSdr(v === "unassigned" ? "" : v)}>
                  <SelectTrigger data-testid="outbound-list-sdr"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">— Unassigned —</SelectItem>
                    {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="flex items-center gap-1">
                  <Droplet className="h-3 w-3 text-sky-700" /> Daily release cap (optional)
                </Label>
                <Input type="number" min="1" value={cap}
                       onChange={(e) => setCap(e.target.value)}
                       placeholder="25 by default"
                       data-testid="outbound-list-cap" />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>CSV file</Label>
                <Input type="file" accept=".csv,text/csv"
                       onChange={(e) => setFile(e.target.files?.[0] || null)}
                       data-testid="outbound-list-file" />
              </div>
            </div>
            <div>
              <Button onClick={upload} disabled={busy || !file || !name.trim()} data-testid="outbound-list-upload">
                <Upload className="h-4 w-4 mr-1" /> {busy ? "Importing…" : "Import"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ----- Apollo.io pull ----- */}
        <Card data-testid="outbound-list-apollo-card" className="border-indigo-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4 text-indigo-600" /> Pull from Apollo.io
              <span className="ml-2 text-[10px] font-mono uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                Live
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-xs text-slate-500">
              Paste an Apollo <strong>Contacts List ID</strong> and we'll fetch its people, dedupe on email,
              and enrol them into Pulse 15. The list must be a <em>Contacts</em> list
              (not People/Prospects — Apollo surfaces different modalities).
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>List name</Label>
                <Input value={aName} onChange={(e) => setAName(e.target.value)}
                       placeholder="e.g. ICP MSPs — Apollo" data-testid="outbound-apollo-name" />
              </div>
              <div className="space-y-1">
                <Label className="flex items-center gap-1">
                  <Megaphone className="h-3 w-3 text-pink-700" /> Campaign (optional)
                </Label>
                <Input value={aCampaign} onChange={(e) => setACampaign(e.target.value)}
                       placeholder="e.g. Barracuda XDR Q1 push" data-testid="outbound-apollo-campaign" />
              </div>
              <div className="space-y-1">
                <Label>Apollo List ID</Label>
                <Input value={aListId} onChange={(e) => setAListId(e.target.value)}
                       placeholder="e.g. 65fe3a..." data-testid="outbound-apollo-list-id" />
              </div>
              <div className="space-y-1">
                <Label>Assign to SDR (optional)</Label>
                <Select value={aSdr || "unassigned"} onValueChange={(v) => setASdr(v === "unassigned" ? "" : v)}>
                  <SelectTrigger data-testid="outbound-apollo-sdr"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">— Unassigned —</SelectItem>
                    {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="flex items-center gap-1">
                  <Droplet className="h-3 w-3 text-sky-700" /> Daily release cap (optional)
                </Label>
                <Input type="number" min="1" value={aCap}
                       onChange={(e) => setACap(e.target.value)}
                       placeholder="25 by default"
                       data-testid="outbound-apollo-cap" />
              </div>
            </div>
            <div>
              <Button onClick={apolloPull}
                      disabled={aBusy || !aName.trim() || !aListId.trim()}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="outbound-apollo-pull">
                <Zap className="h-4 w-4 mr-1" /> {aBusy ? "Pulling…" : "Pull from Apollo"}
              </Button>
            </div>
            <div className="bg-slate-50 border border-slate-200 text-slate-700 rounded p-2 text-[11px] leading-snug space-y-1">
              <div><strong>Find your List ID</strong> in Apollo: open the list, the URL ends with
                {" "}<code className="bg-white px-1 rounded border">contactLabelIds[]=&lt;id&gt;</code>.
                The list must have <code className="bg-white px-1 rounded border">modality: contacts</code>
                (shown as <strong>Contacts List</strong> in the Apollo UI).
              </div>
              <div>If Apollo rejects the ID with a modality error, create a new Contacts List in
                Apollo and move people into it, then paste that ID here.</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Import history</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">List</th>
                <th className="px-3 py-2 text-left">Campaign</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Assigned SDR</th>
                <th className="px-3 py-2 text-right">Imported</th>
                <th className="px-3 py-2 text-right">Queued</th>
                <th className="px-3 py-2 text-right">Released</th>
                <th className="px-3 py-2 text-right">Cap/day</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {lists.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-6 text-center text-sm text-slate-500 italic">No lists yet — import a CSV above.</td></tr>
              )}
              {lists.map((l) => (
                <tr key={l.id} className="border-t border-slate-100" data-testid={`outbound-list-${l.id}`}>
                  <td className="px-3 py-2 font-medium">{l.name}</td>
                  <td className="px-3 py-2">
                    {l.campaign ? (
                      <span className="inline-flex items-center gap-1 bg-pink-50 text-pink-800 border border-pink-200 px-1.5 py-0.5 rounded text-[11px] font-semibold">
                        <Megaphone className="h-3 w-3" /> {l.campaign}
                      </span>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className={`font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${l.source === "apollo" ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>
                      {l.source || "csv"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{l.assigned_sdr_name || <span className="text-slate-400">Unassigned</span>}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-700 font-semibold">{l.imported_count}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-600"
                      data-testid={`outbound-list-queued-${l.id}`}>{l.queued_count ?? l.imported_count}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700">{l.released_count || 0}</td>
                  <td className="px-3 py-2 text-right font-mono text-sky-700">
                    <ListCapCell list={l} onSave={(v) => updateListCap(l, v)} />
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-500">{(l.created_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost"
                                className="text-red-700 hover:text-red-800 hover:bg-red-50"
                                data-testid={`outbound-list-delete-${l.id}`}
                                title="Delete list">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="outbound-list-delete-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete "{l.name}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Removes the list, its {l.imported_count || 0} lead{l.imported_count === 1 ? "" : "s"} and any open Pulse tasks.
                            Historical activity rows (calls, emails, LinkedIn, meetings) are kept so reporting totals stay accurate.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700 text-white"
                            onClick={() => deleteList(l)}
                            data-testid={`outbound-list-delete-confirm-${l.id}`}>
                            Delete list
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

/* ========================== REPORTS TAB ========================== */
function ReportsTab() {
  const [granularity, setGranularity] = useState("day");
  const [data, setData] = useState(null);
  const [range, setRange] = useState(28);   // days

  useEffect(() => {
    (async () => {
      const end = new Date();
      const start = new Date(); start.setDate(end.getDate() - range);
      const toIso = (d) => d.toISOString().slice(0, 10);
      const r = await api.get(`/outbound/reports/activity-per-sdr?start_date=${toIso(start)}&end_date=${toIso(end)}&granularity=${granularity}`);
      setData(r.data);
    })();
  }, [granularity, range]);

  const chans = ["call", "email", "linkedin", "meeting"];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-xs">Range</Label>
        <Select value={String(range)} onValueChange={(v) => setRange(Number(v))}>
          <SelectTrigger className="w-32 h-8" data-testid="outbound-report-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="28">Last 28 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
        <Label className="text-xs">Bucket</Label>
        <Select value={granularity} onValueChange={setGranularity}>
          <SelectTrigger className="w-24 h-8" data-testid="outbound-report-granularity"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Day</SelectItem>
            <SelectItem value="week">Week</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Activity per SDR · {data?.start_date} → {data?.end_date}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="outbound-report-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">SDR</th>
                {chans.map((c) => (
                  <th key={c} className="px-3 py-2 text-right">
                    <span className="inline-flex items-center gap-1 justify-end">
                      {React.createElement(CHANNEL_META[c].icon, { className: `h-3 w-3 ${CHANNEL_META[c].colour}` })}
                      {CHANNEL_META[c].label}
                    </span>
                  </th>
                ))}
                <th className="px-3 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {!data || data.sdrs.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-500 italic">No activity logged in this period yet.</td></tr>
              ) : data.sdrs.map((s) => {
                const total = chans.reduce((acc, c) => acc + s.totals[c], 0);
                return (
                  <tr key={s.sdr_user_id} className="border-t border-slate-100" data-testid={`outbound-report-row-${s.sdr_user_id}`}>
                    <td className="px-3 py-2 font-medium text-slate-900">{s.name}</td>
                    {chans.map((c) => (
                      <td key={c} className="px-3 py-2 text-right">
                        <span className={`font-mono text-sm ${s.totals[c] > 0 ? CHANNEL_META[c].colour : "text-slate-400"}`}>
                          {s.totals[c]}
                        </span>
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right font-mono text-sm font-semibold text-slate-900">{total}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {data?.buckets?.length > 0 && data?.sdrs?.length > 0 && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Daily rhythm</CardTitle></CardHeader>
          <CardContent>
            <SeriesStrip data={data} chans={chans} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SeriesStrip({ data, chans }) {
  // Lightweight inline-SVG stacked bar strip. No chart library needed —
  // Phase 1 just needs a sense of volume per bucket per SDR.
  const w = 24;
  const gap = 4;
  const maxVal = Math.max(1, ...data.sdrs.flatMap((s) => chans.flatMap((c) => s.series[c])));
  return (
    <div className="space-y-3 overflow-x-auto">
      {data.sdrs.map((s) => (
        <div key={s.sdr_user_id} className="min-w-max">
          <div className="text-xs font-semibold text-slate-700 mb-1">{s.name}</div>
          <svg height={80} width={(w + gap) * data.buckets.length}>
            {data.buckets.map((_, i) => {
              let y = 70;
              return chans.map((c) => {
                const h = Math.round((s.series[c][i] / maxVal) * 60);
                const el = (
                  <rect key={`${c}-${i}`}
                        x={i * (w + gap)} y={y - h} width={w} height={h}
                        className={({
                          call: "fill-amber-400",
                          email: "fill-blue-400",
                          linkedin: "fill-indigo-400",
                          meeting: "fill-emerald-500",
                        })[c]}
                        rx={2} />
                );
                y -= h;
                return el;
              });
            })}
            {data.buckets.map((b, i) => (
              <text key={`l-${i}`} x={i * (w + gap) + w / 2} y={78}
                    textAnchor="middle" fontSize={9} fill="#64748b">
                {b.slice(-2)}
              </text>
            ))}
          </svg>
        </div>
      ))}
      <div className="text-xs text-slate-500 flex items-center gap-4 mt-2">
        {chans.map((c) => (
          <span key={c} className="inline-flex items-center gap-1">
            <span className={`inline-block w-2.5 h-2.5 rounded-sm ${({call:"bg-amber-400",email:"bg-blue-400",linkedin:"bg-indigo-400",meeting:"bg-emerald-500"})[c]}`}></span>
            {CHANNEL_META[c].label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ========================== VITALS TAB ========================== */
function VitalsTab({ users }) {
  const [v, setV] = useState(null);
  useEffect(() => { api.get("/outbound/reports/manager-vitals").then((r) => setV(r.data)); }, []);
  const userByUid = useMemo(() => Object.fromEntries(users.map((u) => [u.user_id, u.name])), [users]);
  if (!v) return <div className="text-sm text-slate-500">Loading vitals…</div>;

  const activityTile = (label, counts) => (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</CardTitle></CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-4 gap-2">
          {["call", "email", "linkedin", "meeting"].map((c) => {
            const Icon = CHANNEL_META[c].icon;
            return (
              <div key={c} className="text-center">
                <Icon className={`h-4 w-4 mx-auto ${CHANNEL_META[c].colour}`} />
                <div className="text-xl font-heading font-bold mt-1">{counts[c] || 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{CHANNEL_META[c].label}</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );

  const statuses = v.leads_by_status || {};
  const overdue = v.overdue_by_sdr || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="outbound-vitals-grid">
        {activityTile("Last 7 days", v.activity_7d)}
        {activityTile("Last 30 days", v.activity_30d)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Lead pipeline</CardTitle></CardHeader>
          <CardContent>
            {Object.keys(statuses).length === 0 ? (
              <div className="text-sm text-slate-500 italic">No leads yet.</div>
            ) : (
              <div className="space-y-1.5">
                {Object.entries(statuses).map(([k, n]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <StatusPill status={k} />
                    <span className="font-mono text-slate-700 font-semibold">{n}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1 text-red-800"><AlertTriangle className="h-4 w-4" /> Overdue tasks by SDR</CardTitle></CardHeader>
          <CardContent>
            {overdue.length === 0 ? (
              <div className="text-sm text-emerald-700 flex items-center gap-1 italic"><CheckCircle2 className="h-4 w-4" /> No overdue tasks — team's on cadence.</div>
            ) : (
              <div className="space-y-1.5">
                {overdue.map((o) => (
                  <div key={o.sdr_user_id} className="flex items-center justify-between text-sm">
                    <span>{userByUid[o.sdr_user_id] || o.sdr_user_id || "Unassigned"}</span>
                    <span className="font-mono text-red-700 font-semibold">{o.overdue}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ========================== PAGE SHELL ========================== */
export default function Outbound() {
  const [tab, setTab] = useState("today");
  const [sdrFilter, setSdrFilter] = useState("me");
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-pink-600" />
            <h1 className="text-2xl font-heading font-bold">Pulse Outbound</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">SDR prospecting workspace — Pulse 15 sequence, activity logging and reporting.</p>
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              const r = await api.get("/outbound/sdr-guide.pdf", { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = "Outbound-Pulse-SDR-Guide.pdf";
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not download guide");
            }
          }}
          className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-md shadow-sm"
          data-testid="outbound-download-guide"
          title="A printable 'dummies guide' explaining the Outbound area for new SDRs"
        >
          <StickyNote className="h-3.5 w-3.5 text-pink-600" />
          SDR Dummies Guide
        </button>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="outbound-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.v;
          return (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={`outbound-tab-${t.v}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition ${
                active ? "border-pink-500 text-pink-700" : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              <Icon className="h-4 w-4" /> {t.l}
            </button>
          );
        })}
      </div>

      <div className="pt-2">
        {tab === "today"   && <TodayTab sdrFilter={sdrFilter} onSdrFilter={setSdrFilter} users={users} />}
        {tab === "leads"   && <LeadsTab users={users} />}
        {tab === "lists"   && <ListsTab users={users} />}
        {tab === "reports" && <ReportsTab />}
        {tab === "vitals"  && <VitalsTab users={users} />}
      </div>
    </div>
  );
}
